import { scaleBand, scaleTime } from 'd3-scale';

import { isXAxis } from './is-axis.js';
import type { MinMax } from '../../core/types/min-max.js';
import type { DateScales, NumericScales } from '../../core/types/scales.js';
import {
  getMinMax,
  getScale,
  type buildScale,
  type MinMaxOptions,
} from '../../core/utils/core-scale.js';
import { resolveTimeMinMax } from '../../core/utils/get-min-max-for-scale.js';
import { safeGet } from '../../core/utils/safe-get.js';
import type { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  isLogarithmicAxis,
  isNumericalAxis,
  isTimeAxis,
} from '../types/guards.js';
import type { XYChartState } from '../types/state.js';
import type {
  AxisIdToScaleDomain,
  XYChartAxisInternal,
} from '../types/xy-chart-internals.js';
import type {
  XYChartLinearAxisInternalProps,
  XYChartLogAxisInternalProps,
  XYChartTimeAxisProps,
} from '../types/xy-chart.js';

function generateInitialNumericalScale(
  initialDomain: MinMax,
  axis: XYChartAxisInternal &
    (XYChartLinearAxisInternalProps | XYChartLogAxisInternalProps),
  isXCoordinate: boolean,
  width: number,
  height: number,
) {
  const scaleType = axis.type.split('.')[1] as Parameters<typeof buildScale>[0];
  let min: number, max: number;

  if (axis.interactions.initial) {
    ({ min, max } = initialDomain);
  } else {
    const minMaxByConfig = { min: axis.min, max: axis.max };
    const minMaxOptions: MinMaxOptions = {
      niceMin: isLogarithmicAxis(axis) ? false : axis.min === 'auto',
      niceMax: isLogarithmicAxis(axis) ? false : axis.max === 'auto',
    };
    ({ min, max } = getMinMax(
      scaleType,
      initialDomain,
      minMaxByConfig,
      minMaxOptions,
    ));
  }
  const domain = axis.reversed ? [max, min] : [min, max];
  const range = isXCoordinate ? [0, width] : [height, 0];

  return getScale(scaleType, domain, range);
}

function generateNumericScale(
  initialScale: NumericScales,
  zoomedDomain: MinMax | undefined,
  scaleType: 'log' | 'linear',
) {
  const range = initialScale.range();
  let domain = initialScale.domain();

  if (zoomedDomain) {
    const min = zoomedDomain.min;
    const max = zoomedDomain.max;

    domain = [min, max];
  }

  return getScale(scaleType, domain, range);
}

function generateTimeScale(
  initialScale: DateScales,
  zoomedDomain: MinMax | undefined,
) {
  const range = initialScale.range();

  let domain;
  if (zoomedDomain) {
    const min = zoomedDomain.min;
    const max = zoomedDomain.max;

    domain = [min, max];
  } else {
    domain = initialScale.domain();
  }

  return scaleTime().domain(domain).range(range);
}

function generateInitialTimeScale(
  initialDomain: MinMax,
  axis: XYChartAxisInternal & XYChartTimeAxisProps,
  isXCoordinate: boolean,
  width: number,
  height: number,
) {
  let min: number, max: number;
  if (axis.interactions.initial) {
    ({ min, max } = initialDomain);
  } else {
    const minMaxByConfig = { min: axis.min, max: axis.max };
    ({ min, max } = resolveTimeMinMax(initialDomain, minMaxByConfig));
  }
  const domain = axis.reversed ? [max, min] : [min, max];
  const range = isXCoordinate ? [0, width] : [height, 0];

  const scale = scaleTime().domain(domain).range(range);

  return axis.interactions.initial ? scale : scale.nice();
}

function generateCategoricalScale(
  categoricalValues: Set<string>,
  axis: XYChartAxisInternal,
  isXCoordinate: boolean,
  width: number,
  height: number,
) {
  const range = isXCoordinate ? [0, width] : [0, height];
  const domain = axis.reversed
    ? Array.from(categoricalValues).reverse()
    : categoricalValues;

  return scaleBand(domain, range);
}

/**
 * Generates a current map of scales for each axis ID based on the provided axes configurations.
 *
 * @param axes - The array of axes configurations.
 * @param axisIdToScaleDomain - A map of axis IDs to their initial domains.
 * @param axisIdToZoomedDomain - A map of axis IDs to their current zoomed min max.
 * @param width - The width of the chart area.
 * @param height - The height of the chart area.
 * @returns - A map of axis IDs to their corresponding scales | AxisIdToScale.
 */
export function generateScalesByAxisId(
  axes: XYChartAxisInternal[],
  axisIdToScaleDomain: AxisIdToScaleDomain,
  axisIdToZoomedDomain: XYChartState['currentDomain'] | undefined,
  width: number,
  height: number,
): AxisIdToScales {
  const axisIdToScales: AxisIdToScales = new Map();

  axes.forEach((axis) => {
    const isXCoordinate = isXAxis(axis);

    if (isNumericalAxis(axis)) {
      const initialDomain = ((axis.interactions.initial && {
        min: axis.interactions.initial[0],
        max: axis.interactions.initial[1],
      }) ??
        safeGet(axisIdToScaleDomain, axis.id)) as MinMax;
      const initialScale = generateInitialNumericalScale(
        initialDomain,
        axis,
        isXCoordinate,
        width,
        height,
      );

      const zoomedDomain = axisIdToZoomedDomain?.get(axis.id);

      const currentScale = generateNumericScale(
        initialScale as NumericScales,
        zoomedDomain,
        axis.type.split('.')[1] as Parameters<typeof buildScale>[0],
      );

      axisIdToScales.set(axis.id, { currentScale, initialScale });
    } else if (isTimeAxis(axis)) {
      const initialDomain = ((axis.interactions.initial && {
        min: axis.interactions.initial[0],
        max: axis.interactions.initial[1],
      }) ??
        safeGet(axisIdToScaleDomain, axis.id)) as MinMax;

      const initialScale = generateInitialTimeScale(
        initialDomain,
        axis,
        isXCoordinate,
        width,
        height,
      );
      const zoomedDomain = axisIdToZoomedDomain?.get(axis.id);

      const currentScale = generateTimeScale(
        initialScale as DateScales,
        zoomedDomain,
      );

      axisIdToScales.set(axis.id, { currentScale, initialScale });
    } else {
      const initialDomain = safeGet(
        axisIdToScaleDomain,
        axis.id,
      ) as Set<string>;

      const scale = generateCategoricalScale(
        initialDomain,
        axis,
        isXCoordinate,
        width,
        height,
      );

      axisIdToScales.set(axis.id, {
        initialScale: scale,
        currentScale: scale,
      });
    }
  });

  return axisIdToScales;
}
